import { useStream } from "../contexts/StreamContext";

/**
 * The playback UI. Two faces of the same context-held stream (see
 * contexts/StreamContext): `StreamPlayer` sits in the page column, and
 * `PlayerDock` re-forms it as a bottom bar once the inline one scrolls away.
 */

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
  </svg>
);

const StopIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" fill="currentColor" />
  </svg>
);

/** Decorative bars; they only dance while audio is actually playing. */
const Equalizer = ({ playing }: { playing: boolean }) => (
  <span className={`eq eq--player${playing ? " is-playing" : ""}`} aria-hidden>
    {[0, 1, 2, 3, 4, 5, 6].map((bar) => (
      <span key={bar} />
    ))}
  </span>
);

const PlayButton = ({ small = false }: { small?: boolean }) => {
  const { status, toggle, source, isBlockedMixedContent } = useStream();
  const active = status === "playing" || status === "loading";
  const disabled = !source?.streamUrl || isBlockedMixedContent;

  return (
    <button
      type="button"
      className={`player__play${small ? " player__play--sm" : ""}`}
      onClick={toggle}
      disabled={disabled}
      aria-label={
        active ? "Stop" : `Play${source?.name ? ` ${source.name}` : ""}`
      }
    >
      {active ? <StopIcon /> : <PlayIcon />}
    </button>
  );
};

/** The mono line above the track: what the player is doing right now. */
const statusLabel = (
  status: string,
  blocked: boolean,
  hasStream: boolean,
): string => {
  if (blocked) {
    return "Not playable here";
  }
  if (!hasStream) {
    return "No stream yet";
  }
  switch (status) {
    case "playing":
      return "Now playing";
    case "loading":
      return "Tuning in…";
    case "error":
      return "Stream unavailable";
    default:
      return "Listen live";
  }
};

export const StreamPlayer = () => {
  const { source, status, nowPlaying, isBlockedMixedContent, anchorRef } =
    useStream();
  const hasStream = Boolean(source?.streamUrl);
  const playing = status === "playing";

  const secondary = isBlockedMixedContent
    ? "This station streams over plain HTTP, which browsers block on a secure page. It plays fine in the StreamLover app."
    : status === "error"
      ? "Couldn't reach the stream. Try again in a moment."
      : !hasStream
        ? "This station hasn't published a stream address yet."
        : (nowPlaying.artist ?? source?.name);

  return (
    <section className="player" ref={anchorRef} aria-label="Station player">
      <PlayButton />
      <div className="player__meta">
        <p className="player__eyebrow mono">
          {playing && <i className="live-dot" />}
          {statusLabel(status, isBlockedMixedContent, hasStream)}
        </p>
        <p className="player__title">
          {nowPlaying.track ?? nowPlaying.show ?? source?.name ?? "—"}
        </p>
        {secondary && <p className="player__sub">{secondary}</p>}
      </div>
      <div className="player__controls">
        <Equalizer playing={playing} />
      </div>
    </section>
  );
};

export const PlayerDock = () => {
  const { source, status, nowPlaying, isDocked } = useStream();
  if (!isDocked || !source) {
    return null;
  }

  const line = [nowPlaying.track ?? nowPlaying.show, nowPlaying.artist]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="playerbar" aria-label="Player">
      <div className="playerbar__inner">
        <PlayButton small />
        {source.logoUrl && (
          <img className="playerbar__logo" src={source.logoUrl} alt="" />
        )}
        <div className="playerbar__meta">
          <p className="playerbar__station mono">
            {status === "playing" && <i className="live-dot" />}
            {source.href ? (
              <a href={source.href}>{source.name}</a>
            ) : (
              source.name
            )}
          </p>
          {line && <p className="playerbar__track">{line}</p>}
        </div>
        <div className="player__controls">
          <Equalizer playing={status === "playing"} />
        </div>
      </div>
    </section>
  );
};

export default StreamPlayer;
